import type {
  ExtratoFinanceiroApiResponse,
  ExtratoFinanceiroView,
  MovimentacaoFinanceiraApi,
  MovimentacaoFinanceiraTipoVisual,
  MovimentacaoFinanceiraView,
} from "../types/extrato-financeiro.types";
import { formatarDataMovimentacaoFinanceiro } from "./formatar-data-financeiro";
import {
  descricaoExtratoPrincipal,
  subtituloExtrato,
} from "./extrato-movimentacao-labels";
import { toNumber } from "./map-dre-api-to-view";

const TIPO_LABELS: Record<string, string> = {
  ENTRADA: "Entrada",
  SAIDA: "Saída",
  TRANSFERENCIA: "Transferência",
  ESTORNO: "Estorno",
  TAXA: "Taxa",
  AJUSTE: "Ajuste",
};

const ORIGEM_LABELS: Record<string, string> = {
  WEBHOOK_EFI: "Webhook EFI",
  MANUAL: "Manual",
  AJUSTE_SISTEMA: "Ajuste sistema",
  IMPORTACAO: "Importação",
};

function listaItens(api: ExtratoFinanceiroApiResponse): MovimentacaoFinanceiraApi[] {
  return api.data ?? api.items ?? api.movimentacoes ?? api.registros ?? [];
}

function tipoParaVisual(tipoRaw: string): MovimentacaoFinanceiraTipoVisual {
  const t = tipoRaw.toUpperCase();
  if (t === "ENTRADA") return "entrada";
  if (t === "SAIDA") return "saida";
  if (t === "TAXA") return "taxa";
  if (t === "TRANSFERENCIA") return "transferencia";
  if (t === "AJUSTE") return "ajuste";
  if (t === "ESTORNO") return "saida";
  return "neutro";
}

function labelTipo(tipoRaw: string): string {
  const u = tipoRaw.toUpperCase();
  return TIPO_LABELS[u] ?? tipoRaw;
}

function labelOrigem(origemRaw: string): string {
  const u = origemRaw.toUpperCase();
  return ORIGEM_LABELS[u] ?? origemRaw;
}

function labelData(iso: string | null | undefined): string {
  return formatarDataMovimentacaoFinanceiro(iso);
}

function valorAssinadoApiRow(
  tipoRaw: string,
  valorAbs: number,
): { valor: number; valorAbsoluto: number } {
  const tv = tipoParaVisual(tipoRaw);
  const abs = Math.abs(valorAbs);
  if (tv === "entrada") return { valor: abs, valorAbsoluto: abs };
  if (tv === "saida" || tv === "taxa") return { valor: -abs, valorAbsoluto: abs };
  if (tv === "transferencia") return { valor: abs, valorAbsoluto: abs };
  if (tv === "ajuste") return { valor: abs, valorAbsoluto: abs };
  return { valor: abs, valorAbsoluto: abs };
}

function mapLinha(row: MovimentacaoFinanceiraApi): MovimentacaoFinanceiraView | null {
  const id = row.id?.trim();
  if (!id) return null;

  const tipoRaw = String(row.tipo ?? "").trim() || "DESCONHECIDO";
  const tipoVisual = tipoParaVisual(tipoRaw);
  const baseAbs = toNumber(row.valorAbsoluto ?? row.valor);
  const { valor, valorAbsoluto } = valorAssinadoApiRow(tipoRaw, baseAbs);

  const centroId = row.centroCustoId ?? null;
  const semCentro = centroId == null || centroId === "";

  const origemRaw = String(row.origem ?? "").trim() || "—";

  const base: MovimentacaoFinanceiraView = {
    id,
    contaFinanceiraNome: row.contaFinanceiraNome?.trim() || "—",
    contaDestinoNome: row.contaDestinoNome?.trim() ?? null,
    centroCustoNome: semCentro ? "Sem centro" : row.centroCustoNome?.trim() || "—",
    pedidoId: row.pedidoId ?? null,
    pedidoReferencia: row.pedidoReferencia?.trim() ?? null,
    inscricaoNumero: row.inscricaoNumero?.trim() ?? null,
    tituloReceberId: row.tituloReceberId ?? null,
    tituloReceberDescricao: row.tituloReceberDescricao?.trim() ?? null,
    tituloPagarId: row.tituloPagarId ?? null,
    tituloPagarDescricao: row.tituloPagarDescricao?.trim() ?? null,
    tituloPagarFornecedor: row.tituloPagarFornecedor?.trim() ?? null,
    tituloPagarCategoria: row.tituloPagarCategoria ?? null,
    tituloPagarVencimento: row.tituloPagarVencimento ?? null,
    pagamentoId: row.pagamentoId ?? null,
    tipo: tipoRaw,
    tipoLabel: labelTipo(tipoRaw),
    tipoVisual,
    categoria: row.categoria?.trim() || "—",
    origem: origemRaw,
    origemLabel: origemRaw !== "—" ? labelOrigem(origemRaw) : "—",
    descricao: row.descricao?.trim() || "—",
    descricaoPrincipal: "",
    descricaoSubtitulo: null,
    documento: row.documento?.trim() ?? null,
    identificadorExterno: row.identificadorExterno?.trim() ?? null,
    valor,
    valorAbsoluto,
    dataMovimentacao: row.dataMovimentacao ?? row.movimentadoEm ?? null,
    dataMovimentacaoLabel: labelData(row.dataMovimentacao ?? row.movimentadoEm),
    semCentro,
    observacao: row.observacao?.trim() ?? null,
  };

  return {
    ...base,
    descricaoPrincipal: descricaoExtratoPrincipal(base),
    descricaoSubtitulo: subtituloExtrato(base),
  };
}

function resumoFallback(itens: MovimentacaoFinanceiraView[]): ExtratoFinanceiroView["resumo"] {
  let totalEntradas = 0;
  let totalSaidas = 0;
  let totalTaxas = 0;
  let totalTransferencias = 0;
  let totalAjustes = 0;
  let quantidadeSemCentro = 0;

  for (const r of itens) {
    if (r.semCentro) quantidadeSemCentro += 1;
    const abs = r.valorAbsoluto;
    switch (r.tipoVisual) {
      case "entrada":
        totalEntradas += abs;
        break;
      case "saida":
        totalSaidas += abs;
        break;
      case "taxa":
        totalTaxas += abs;
        totalSaidas += abs;
        break;
      case "transferencia":
        totalTransferencias += abs;
        break;
      case "ajuste":
        totalAjustes += abs;
        break;
      default:
        totalAjustes += abs;
        break;
    }
  }

  const saldoMovimentado = totalEntradas - totalSaidas;

  return {
    totalEntradas,
    totalSaidas,
    saldoMovimentado,
    totalTaxas,
    totalTransferencias,
    totalAjustes,
    quantidadeMovimentacoes: itens.length,
    quantidadeSemCentro,
  };
}

export function mapExtratoFinanceiroApiToView(
  api: ExtratoFinanceiroApiResponse | null,
): ExtratoFinanceiroView | null {
  if (!api) return null;

  const rawList = listaItens(api);
  const itens = rawList
    .map((r) => mapLinha(r))
    .filter((x): x is MovimentacaoFinanceiraView => x != null);

  const total = typeof api.total === "number" ? api.total : itens.length;
  const page = typeof api.page === "number" ? api.page : 1;
  const limit = typeof api.limit === "number" ? api.limit : 20;
  const totalPages =
    typeof api.totalPages === "number"
      ? api.totalPages
      : Math.max(1, Math.ceil(total / Math.max(1, limit)));

  const rs = api.resumo;
  const resumo =
    rs != null
      ? {
          totalEntradas: toNumber(rs.totalEntradas),
          totalSaidas: toNumber(rs.totalSaidas),
          saldoMovimentado: toNumber(rs.saldoMovimentado),
          totalTaxas: toNumber(rs.totalTaxas),
          totalTransferencias: toNumber(rs.totalTransferencias),
          totalAjustes: toNumber(rs.totalAjustes),
          quantidadeMovimentacoes:
            typeof rs.quantidadeMovimentacoes === "number"
              ? rs.quantidadeMovimentacoes
              : total,
          quantidadeSemCentro:
            typeof rs.quantidadeSemCentro === "number" ? rs.quantidadeSemCentro : 0,
        }
      : resumoFallback(itens);

  return {
    itens,
    total,
    page,
    limit,
    totalPages,
    resumo,
  };
}
